import { BehaviorRuntimeHandle, DataProgramHandle } from 'fluveria-engine-wasm';
import type { DataGraphDef, DataGraphInstance } from '../graph/dataGraph.js';
import type { ParamStore, ParamValue } from '../graph/nodeDefs.js';
import { layerLayoutMetrics, type LayoutSession, type SceneLayoutContext } from '../scene/layoutEngine.js';
import type { Vec2 } from '../scene/layoutMath.js';
import type { AppState } from '../state/appState.js';
import type { ViewportResolution } from './viewportResolution.js';
import { screenToWorld } from './viewportCoords.js';
import type { EventRuntimeManager } from './eventRuntime.js';
import { toEngineSnapshot } from './file.js';
import { WasmProgramCache } from './wasmProgramCache.js';

export interface DataRuntimeEvaluation {
  globalParams: ParamStore;
  /** Each node's parameters, by its path (layoutEngine's `nodePath`). */
  behaviorParams: Record<string, ParamStore>;
  /** What the behaviours scrolled, folded, turned, showed and hid: what the frame is laid out and drawn in. */
  session: LayoutSession;
  active: boolean;
}

export interface DataRuntimeMetrics {
  evaluatedInstances: number;
  cacheHits: number;
}

/**
 * A key typed into the field that holds the keyboard, as the engine's
 * BehaviorRuntimeHandle names it: `text` and `compose` carry their text,
 * the rest are editing keys; `shift` extends the selection, `word` steps a word.
 */
export interface TypedKey {
  key: 'text' | 'compose' | 'backspace' | 'delete' | 'left' | 'right' | 'up' | 'down'
    | 'home' | 'end' | 'enter' | 'selectAll' | 'undo' | 'redo';
  text?: string;
  shift?: boolean;
  word?: boolean;
}

/** What the field holding the keyboard types in, as the last tick left it. */
export interface EditedText {
  node: string;
  text: string;
  caretByte: number;
  selection: [number, number] | null;
  composing: [number, number] | null;
}

interface BehaviorTickOutput {
  changed: boolean;
  active: boolean;
  wakeAt: number | null;
  params: Record<string, Record<string, ParamValue>>;
  scrollOffsets: Record<string, Vec2>;
  revealWrites: Array<{ frame: string; shown: number }>;
  shownWrites: Array<{ node: string; shown: boolean }>;
  rotationWrites: Array<{ node: string; rotation: number }>;
  hovered: string[];
  textFocus: string | null;
  editedText: EditedText | null;
  controlOver: boolean;
  textCursorOver: boolean;
  diagnostics: string[];
}

interface DataDependencies {
  stateKeys: string[];
  sourceFields: Record<string, string[]>;
  queryOutputs: Record<string, string[]>;
  inputs: string[];
  recordFields: string[];
  usesTime: boolean;
  usesPointer: boolean;
  usesHover: boolean;
  usesPressed: boolean;
}

interface CachedEvaluation {
  graphSignature: string;
  inputSignature: string;
  params: ParamStore;
  active: boolean;
}

function emptySession(): LayoutSession {
  return { scrollOffsets: {}, reveals: {}, rotations: {}, shown: {} };
}

/**
 * Runs the running document's data graphs: the document's own (`data`),
 * evaluated here, and every node's behaviour, ticked by the engine's
 * interaction runtime (BehaviorRuntimeHandle) as the native editor ticks it --
 * the nodes found, the hover and the press, the frames a `Scroll` reads, the
 * folds a `Reveal` reads, the press beside a node, the keyboard a `TextEdit`
 * types from, are the engine's. This side keeps what the engine asks the host
 * to keep: the pointer and the keys since the last tick, and the session
 * (where frames look, what is folded, turned, shown), which it hands to the
 * next tick and to the frame's layout and drawing.
 */
export class DataRuntimeManager {
  private readonly programs = new WasmProgramCache<DataProgramHandle>();
  private behaviors: BehaviorRuntimeHandle | null = null;
  private documentSignature = '';
  private started = performance.now();
  private lastTick = this.started;
  /** Where the pointer is now, in the document's world; null off the document. */
  private pointer: Vec2 | null = null;
  /** Where it last was: a graph that places by the pointer keeps it there. */
  private lastPointer: Vec2 | null = null;
  private pointerDown = false;
  /** The button went down since the last tick: a click between two ticks is still a click. */
  private pointerPressed = false;
  private keys: TypedKey[] = [];
  private behaviorSession: LayoutSession = emptySession();
  private lastBehaviorParams: Record<string, ParamStore> = {};
  private textFocusPath: string | null = null;
  private edited: EditedText | null = null;
  private wakeTimer: ReturnType<typeof setTimeout> | null = null;
  private dataSources: Record<string, unknown> = {};
  /** Sources whose records the behaviours have not been handed since they changed. */
  private readonly unhandedSources = new Set<string>();
  private sourceDocuments: Record<string, unknown> = {};
  private queryOutputs: Record<string, Record<string, unknown>> = {};
  private queryOutputSignature = '';
  private queryResolver: (() => Record<string, Record<string, unknown>>) | null = null;
  private scheduleRender: (() => void) | null = null;
  private readonly dirtyStateKeys = new Set<string>();
  private readonly dirtySourceFields = new Map<string, Set<string>>();
  private readonly programDependencies = new Map<string, { signature: string; dependencies: DataDependencies }>();
  private readonly evaluations = new Map<string, CachedEvaluation>();
  private readonly unsubscribeState?: () => void;
  private frameMetrics: DataRuntimeMetrics = { evaluatedInstances: 0, cacheHits: 0 };
  /** What the behaviours said, once each. */
  readonly diagnostics: string[] = [];

  constructor(
    private readonly appState: AppState,
    private readonly eventRuntime?: EventRuntimeManager,
  ) {
    this.unsubscribeState = eventRuntime?.subscribeChanged(keys => {
      keys.forEach(key => this.dirtyStateKeys.add(key));
      this.scheduleRender?.();
    });
  }

  setScheduleRender(callback: () => void): void {
    this.scheduleRender = callback;
  }

  setDataSources(sources: Record<string, unknown>): void {
    this.dataSources = sources;
    Object.keys(sources).forEach(sourceId => this.markSourceChanged(sourceId));
    this.scheduleRender?.();
  }

  setLoadedDataSources(records: Record<string, unknown>, documents: Record<string, unknown>): void {
    this.dataSources = records;
    this.sourceDocuments = documents;
    Object.keys(records).forEach(sourceId => this.markSourceChanged(sourceId));
    this.scheduleRender?.();
  }

  setDataSource(sourceId: string, records: unknown, document?: unknown, changedFields: readonly string[] = ['*']): void {
    this.dataSources = { ...this.dataSources, [sourceId]: records };
    if (document !== undefined) this.sourceDocuments = { ...this.sourceDocuments, [sourceId]: document };
    this.markSourceChanged(sourceId, changedFields);
    this.scheduleRender?.();
  }

  setQueryOutputs(outputs: Record<string, Record<string, unknown>>): void {
    const signature = JSON.stringify(outputs);
    if (signature === this.queryOutputSignature) return;
    this.queryOutputSignature = signature;
    this.queryOutputs = outputs;
    this.evaluations.clear();
    this.scheduleRender?.();
  }

  setQueryResolver(resolver: () => Record<string, Record<string, unknown>>): void { this.queryResolver = resolver; }

  markSourceChanged(sourceId: string, fields: readonly string[] = ['*']): void {
    const dirty = this.dirtySourceFields.get(sourceId) ?? new Set<string>();
    fields.forEach(field => dirty.add(field));
    this.dirtySourceFields.set(sourceId, dirty);
    this.unhandedSources.add(sourceId);
  }

  /** Session-only records loaded by the active source resolver. */
  getDataSources(): Readonly<Record<string, unknown>> {
    return this.dataSources;
  }

  getSourceDocument(sourceId: string): unknown {
    return this.sourceDocuments[sourceId];
  }

  setPointer(point: Vec2 | null, down = this.pointerDown): void {
    this.pointer = point;
    if (point) this.lastPointer = point;
    this.setPointerDown(down);
  }

  setPointerDown(down: boolean): void {
    if (down && !this.pointerDown) this.pointerPressed = true;
    this.pointerDown = down;
    this.scheduleRender?.();
  }

  /** The node that holds the keyboard, by path, as the last tick left it: what the host hands keys to. */
  textFocus(): string | null {
    return this.textFocusPath;
  }

  /** What the field holding the keyboard types in, as the last tick left it. */
  editedText(): EditedText | null {
    return this.edited;
  }

  /** A key for the field that holds the keyboard, typed on the next tick. */
  type(key: TypedKey): void {
    this.keys.push(key);
    this.scheduleRender?.();
  }

  /** Take the keyboard from the field that holds it (Escape). */
  releaseTextFocus(): void {
    this.behaviors?.releaseTextFocus();
    this.textFocusPath = null;
    this.edited = null;
    this.scheduleRender?.();
  }

  /**
   * Turn the wheel by `delta` (world units) at `point`, over the frame the
   * last tick laid out: the innermost frame under the pointer that can still
   * move takes it, as in the editor. Whether the document took the turn --
   * moved a frame, or is over one with something to scroll -- so the host
   * leaves the canvas where it is.
   */
  wheel(point: Vec2, delta: Vec2): boolean {
    if (!this.behaviors) return false;
    const turned = this.behaviors.wheel({
      pointer: point,
      delta,
      scrollOffsets: this.behaviorSession.scrollOffsets,
      shown: this.behaviorSession.shown,
      now: performance.now() / 1000,
    }) as { scrollOffsets: Record<string, Vec2>; moved: string[]; taken: boolean };
    if (turned.moved.length) {
      this.behaviorSession = { ...this.behaviorSession, scrollOffsets: turned.scrollOffsets };
      this.scheduleRender?.();
    }
    return turned.taken;
  }

  /** The session the behaviours left: what the frame is laid out and drawn in. */
  session(): LayoutSession {
    return this.behaviorSession;
  }

  reset(): void {
    this.programs.clear();
    this.behaviors?.reset();
    this.behaviorSession = emptySession();
    this.lastBehaviorParams = {};
    this.textFocusPath = null;
    this.edited = null;
    this.keys = [];
    this.started = performance.now();
    this.lastTick = this.started;
    this.evaluations.clear();
    this.programDependencies.clear();
  }

  /** Starts a new interactive sampling interval without resetting runtime state. */
  resume(): void {
    this.lastTick = performance.now();
    this.pointer = null;
    this.pointerDown = false;
    this.pointerPressed = false;
    this.keys = [];
  }

  /**
   * One tick: the document's data graph and every node's behaviour.
   * `layoutContext` is what the frame lays the document out in -- the layers
   * it measures -- and `viewport` the box it is laid out in.
   */
  evaluate(
    layoutContext: SceneLayoutContext,
    viewport: ViewportResolution,
  ): DataRuntimeEvaluation {
    this.frameMetrics = { evaluatedInstances: 0, cacheHits: 0 };
    this.eventRuntime?.syncSchema(this.appState.runtimeState);
    if (this.queryResolver) this.setQueryOutputs(this.queryResolver());
    const now = performance.now();
    const elapsed = (now - this.started) / 1000;
    const delta = Math.min((now - this.lastTick) / 1000, 1 / 15);
    this.lastTick = now;
    const globalParams = this.evaluateInstance('$global', this.appState.data, {
      elapsed,
      delta,
      pointerLocal: this.pointer ?? [0, 0],
      pointerNormalized: this.pointer
        ? [this.pointer[0] / Math.max(viewport.w, Number.EPSILON), this.pointer[1] / Math.max(viewport.h, Number.EPSILON)]
        : [0, 0],
      pointerDown: this.pointerDown,
      hover: this.pointer !== null,
      pressed: this.pointerDown,
    }, undefined);

    const behaviors = this.tickBehaviors(layoutContext, viewport, elapsed, delta);
    const active = globalParams.active || behaviors.active;
    if (active || behaviors.changed) this.scheduleRender?.();
    this.dirtyStateKeys.clear();
    this.dirtySourceFields.clear();
    return { globalParams: globalParams.params, behaviorParams: this.lastBehaviorParams, session: this.behaviorSession, active };
  }

  /** The engine's interaction runtime over the document as it stands now. */
  private behaviorRuntime(): BehaviorRuntimeHandle {
    this.behaviors ??= new BehaviorRuntimeHandle();
    const snapshot = toEngineSnapshot(this.appState);
    const signature = JSON.stringify(snapshot);
    if (signature !== this.documentSignature) {
      this.behaviors.setDocument(snapshot);
      this.documentSignature = signature;
    }
    for (const sourceId of this.unhandedSources) {
      if (sourceId in this.dataSources) this.behaviors.setRecords(sourceId, this.dataSources[sourceId] ?? null);
    }
    this.unhandedSources.clear();
    return this.behaviors;
  }

  private tickBehaviors(
    layoutContext: SceneLayoutContext,
    viewport: ViewportResolution,
    elapsed: number,
    delta: number,
  ): { active: boolean; changed: boolean } {
    const runtime = this.behaviorRuntime();
    // The box and the layers' sizes the frame lays the document out by, the
    // layers measured with what the behaviours gave them on the last tick.
    const rootSpace = { min: { w: 0, h: 0 }, max: { w: viewport.w, h: viewport.h } };
    const layerMetrics = layerLayoutMetrics(this.appState.scene, rootSpace, {
      ...layoutContext,
      behaviorParams: this.lastBehaviorParams,
      behaviorPathPrefix: '',
    });
    const input = {
      elapsed,
      delta,
      rootSpace,
      layerMetrics,
      scrollOffsets: this.behaviorSession.scrollOffsets,
      reveals: this.behaviorSession.reveals,
      rotations: this.behaviorSession.rotations,
      shown: this.behaviorSession.shown,
      pointer: this.lastPointer,
      pointerPresent: this.pointer !== null,
      pointerDown: this.pointerDown,
      pointerPressed: this.pointerPressed,
      keys: this.keys,
    };
    this.pointerPressed = false;
    this.keys = [];
    const state = this.eventRuntime?.handle();
    const out = (state ? runtime.tickWithState(input, state) : runtime.tick(input)) as BehaviorTickOutput;

    this.lastBehaviorParams = out.params as Record<string, ParamStore>;
    const session: LayoutSession = {
      scrollOffsets: out.scrollOffsets,
      reveals: { ...this.behaviorSession.reveals },
      rotations: { ...this.behaviorSession.rotations },
      shown: { ...this.behaviorSession.shown },
    };
    for (const write of out.revealWrites) session.reveals[write.frame] = write.shown;
    for (const write of out.shownWrites) session.shown[write.node] = write.shown;
    for (const write of out.rotationWrites) session.rotations[write.node] = write.rotation;
    const moved = out.revealWrites.length + out.shownWrites.length + out.rotationWrites.length > 0
      || JSON.stringify(out.scrollOffsets) !== JSON.stringify(this.behaviorSession.scrollOffsets);
    this.behaviorSession = session;
    this.textFocusPath = out.textFocus;
    this.edited = out.editedText;
    for (const diagnostic of out.diagnostics) {
      this.diagnostics.push(diagnostic);
      console.warn(diagnostic);
    }
    // A caret blinks with nothing else asking for a frame: wake for it.
    if (this.wakeTimer !== null) clearTimeout(this.wakeTimer);
    this.wakeTimer = out.wakeAt === null || out.active
      ? null
      : setTimeout(() => {
        this.wakeTimer = null;
        this.scheduleRender?.();
      }, Math.max(0, (out.wakeAt - elapsed) * 1000) + 1);
    return { active: out.active, changed: out.changed || moved };
  }

  private program(graphId: string, graph: DataGraphDef): DataProgramHandle | null {
    try {
      const handle = this.programs.get(graphId, graph, () => new DataProgramHandle(graph));
      const signature = JSON.stringify(graph);
      if (this.programDependencies.get(graphId)?.signature !== signature) {
        this.programDependencies.set(graphId, { signature, dependencies: handle.dependencies() as DataDependencies });
      }
      return handle;
    } catch (error) {
      console.error(`Data Graph '${graphId}' failed to compile`, error);
      return null;
    }
  }

  private evaluateInstance(
    instanceKey: string,
    instance: DataGraphInstance | null | undefined,
    context: {
      elapsed: number;
      delta: number;
      pointerLocal: Vec2;
      pointerNormalized: Vec2;
      pointerDown: boolean;
      hover: boolean;
      pressed: boolean;
    },
    ownerKey: string | undefined,
  ): { params: ParamStore; active: boolean } {
    if (!instance) return { params: {}, active: false };
    const graph = this.appState.dataGraphs[instance.use];
    const program = graph ? this.program(instance.use, graph) : null;
    if (!program) return { params: {}, active: false };
    const programInfo = this.programDependencies.get(instance.use)!;
    const cacheKey = `${instance.use}\u0000${instanceKey}`;
    const inputSignature = JSON.stringify({ inputs: instance.inputs ?? {} });
    const cached = this.evaluations.get(cacheKey);
    if (cached
      && cached.graphSignature === programInfo.signature
      && cached.inputSignature === inputSignature
      && !cached.active
      && !this.dependenciesDirty(programInfo.dependencies)) {
      this.frameMetrics.cacheHits++;
      return { params: cached.params, active: false };
    }
    this.frameMetrics.evaluatedInstances++;
    const runtimeState = this.eventRuntime?.handle();
    const inputContext = {
      ...context,
      inputs: instance.inputs ?? {},
      dataSources: this.dataSources,
      queries: this.queryOutputs,
      record: {},
    };
    const result = (runtimeState
      ? program.evaluateWithState(instanceKey, inputContext, runtimeState, ownerKey)
      : program.evaluate(instanceKey, inputContext)) as {
      outputs: Record<string, ParamValue>;
      active: boolean;
      usesTime: boolean;
    };
    const params: ParamStore = {};
    for (const [output, target] of Object.entries(instance.bindings)) {
      const value = result.outputs[output];
      if (value !== undefined) params[target] = value;
    }
    const active = result.active || result.usesTime;
    this.evaluations.set(cacheKey, { graphSignature: programInfo.signature, inputSignature, params, active });
    return { params, active };
  }

  private dependenciesDirty(dependencies: DataDependencies): boolean {
    if (dependencies.usesTime || dependencies.usesPointer || dependencies.usesHover || dependencies.usesPressed) return true;
    if (dependencies.stateKeys.some(key => this.dirtyStateKeys.has(key))) return true;
    return Object.entries(dependencies.sourceFields).some(([sourceId, fields]) => {
      const dirty = this.dirtySourceFields.get(sourceId);
      return dirty?.has('*') || fields.some(field => dirty?.has(field));
    });
  }

  metrics(): Readonly<DataRuntimeMetrics> {
    return this.frameMetrics;
  }

  dispose(): void {
    this.unsubscribeState?.();
    this.programs.clear();
    this.evaluations.clear();
    if (this.wakeTimer !== null) clearTimeout(this.wakeTimer);
    this.behaviors?.free();
    this.behaviors = null;
  }
}

/**
 * A key of the keyboard as the field holding it takes it, or null for one it
 * does not: the editing keys, with Shift to select and Ctrl (Alt on a Mac) to
 * step a word, Ctrl+A, Ctrl+Z and Ctrl+Shift+Z, and a character typed.
 */
export function typedKeyOf(event: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>): TypedKey | null {
  const command = event.ctrlKey || event.metaKey;
  const shift = event.shiftKey;
  const word = event.ctrlKey || event.altKey;
  if (command) {
    switch (event.key.toLowerCase()) {
      case 'a': return { key: 'selectAll' };
      case 'z': return { key: shift ? 'redo' : 'undo' };
      case 'y': return { key: 'redo' };
    }
  }
  const editing: Record<string, TypedKey['key']> = {
    Backspace: 'backspace',
    Delete: 'delete',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    ArrowUp: 'up',
    ArrowDown: 'down',
    Home: 'home',
    End: 'end',
    Enter: 'enter',
  };
  const named = editing[event.key];
  if (named) return { key: named, shift, word };
  // A character: one grapheme, not a named key, and no command held.
  if (!command && !event.altKey && [...event.key].length === 1) return { key: 'text', text: event.key };
  return null;
}

/** The part of `text` between two of its UTF-8 bytes: where the engine says a selection stands. */
export function textBetweenBytes(text: string, start: number, end: number): string {
  const bytes = new TextEncoder().encode(text);
  return new TextDecoder().decode(bytes.slice(start, end));
}

export function initDataRuntimePointer(
  runtime: DataRuntimeManager,
  getResolution: () => ViewportResolution,
  isEnabled: () => boolean,
): void {
  const stage = document.getElementById('viewport-stage');
  if (!stage) return;
  const update = (event: PointerEvent): void => {
    if (!isEnabled()) {
      runtime.setPointer(null);
      runtime.setPointerDown(false);
      return;
    }
    runtime.setPointer(screenToWorld(event.clientX, event.clientY, getResolution()));
  };
  stage.addEventListener('pointermove', update);
  stage.addEventListener('pointerleave', () => {
    if (isEnabled()) runtime.setPointer(null);
  });
  stage.addEventListener('pointerdown', event => {
    update(event);
    if (event.button === 0) runtime.setPointerDown(true);
  });
  window.addEventListener('pointerup', event => {
    if (event.button === 0 && isEnabled()) runtime.setPointerDown(false);
  });
  // The wheel over a scroller is the document's: it scrolls the frame, and
  // the canvas behind it stays where it is.
  stage.addEventListener('wheel', event => {
    if (!isEnabled()) return;
    const resolution = getResolution();
    const point = screenToWorld(event.clientX, event.clientY, resolution);
    const past = screenToWorld(event.clientX + event.deltaX, event.clientY + event.deltaY, resolution);
    if (!point || !past) return;
    if (runtime.wheel(point, [past[0] - point[0], past[1] - point[1]])) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, { capture: true, passive: false });
  // While a field holds the keyboard, keys are the field's -- Escape gives
  // the keyboard back before it stands the document -- and a paste types.
  window.addEventListener('keydown', event => {
    if (!isEnabled() || runtime.textFocus() === null) return;
    if (event.key === 'Escape') {
      runtime.releaseTextFocus();
    } else if ((event.ctrlKey || event.metaKey) && ['c', 'x'].includes(event.key.toLowerCase())) {
      const edited = runtime.editedText();
      const selected = edited?.selection ? textBetweenBytes(edited.text, ...edited.selection) : '';
      if (selected) void navigator.clipboard?.writeText(selected);
      if (selected && event.key.toLowerCase() === 'x') runtime.type({ key: 'delete' });
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
      return; // the paste event types it
    } else {
      const key = typedKeyOf(event);
      if (!key) return;
      runtime.type(key);
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }, { capture: true });
  window.addEventListener('paste', event => {
    if (!isEnabled() || runtime.textFocus() === null) return;
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (text) runtime.type({ key: 'text', text });
    event.preventDefault();
  });
}
